/* eslint-disable no-unused-vars */
import { E164Number } from "libphonenumber-js/core";
import Image from "next/image";
import ReactDatePicker, { registerLocale } from "react-datepicker";
import { Control } from "react-hook-form";
import PhoneInput from "react-phone-number-input";
import "react-datepicker/dist/react-datepicker.css";
import { fr } from 'date-fns/locale/fr';
registerLocale('fr', fr)


import { Checkbox } from "./ui/checkbox";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { cn } from "@/lib/utils";

export enum FormFieldType {
  INPUT = "input",
  TEXTAREA = "textarea",
  PHONE_INPUT = "phoneInput",
  CHECKBOX = "checkbox",
  DATETIME_PICKER = "datetimePicker",
  DATE_PICKER = "datePicker",
  SELECT = "select",
  SKELETON = "skeleton",
  SWITCH = "switch",
}

interface CustomProps {
  control: Control<any>;
  name: string;
  label?: string;
  placeholder?: string;
  iconSrc?: string;
  iconAlt?: string;
  disabled?: boolean;
  dateFormat?: string;
  showTimeSelect?: boolean;
  children?: React.ReactNode;
  renderSkeleton?: (field: any) => React.ReactNode;
  fieldType: FormFieldType;
  onChangeDate?: (value: Date | null) => void;
  onChangeInput?: (value: string | null) => void;
  maxLength?: number;
  className?: string;
}

const RenderInput = ({ field, props }: { field: any; props: CustomProps }) => {
  switch (props.fieldType) {
    case FormFieldType.INPUT:
      return (
        <div className="flex rounded-md border border-dark-500 bg-dark-400">
          {props.iconSrc && (
            <Image
              src={props.iconSrc}
              height={24}
              width={24}
              alt={props.iconAlt || "icon"}
              className="ml-2"
            />
          )}
          <FormControl>
            <Input
              placeholder={props.placeholder}
              {...field}
              className={cn("shad-input border-0", props.className )}
              maxLength={props.maxLength || 100}
            />
          </FormControl>
        </div>
      );
    case FormFieldType.TEXTAREA:
      return (
        <FormControl>
          <Textarea
            placeholder={props.placeholder}
            {...field}
            className="shad-textArea"
            disabled={props.disabled}
          />
        </FormControl>
      );
    case FormFieldType.PHONE_INPUT:
      return (
        <FormControl>
          <PhoneInput
            defaultCountry="US"
            placeholder={props.placeholder}
            international
            withCountryCallingCode
            value={field.value as E164Number | undefined}
            onChange={field.onChange}
            className="input-phone"
          />
        </FormControl>
      );
    case FormFieldType.CHECKBOX:
      return (
        <FormControl>
          <div className="flex items-center gap-4">
            <Checkbox
              id={props.name}
              checked={field.value}
              onCheckedChange={field.onChange}
              className="h-6 w-6 border-2 border-blue-600"

            />
            <label htmlFor={props.name} className="checkbox-label">
              {props.label}
            </label>
          </div>
        </FormControl>
      );
    case FormFieldType.DATETIME_PICKER:
      return (
        <div className="flex rounded-md border border-dark-500 bg-white p-2 text-sm items-center gap-2 font-medium">
          <Image
            src="/icons/calendar.svg"
            height={24}
            width={24}
            alt="user"
            className="ml-2"
          />
          <FormControl>
            <ReactDatePicker

              locale="fr"
              placeholderText={props.placeholder}
              isClearable={true}
              showTimeSelect={props.showTimeSelect ?? false}
              timeIntervals={15}
              selected={field.value}
              onChange={(date) => {
                if (props.onChangeDate) {
                  props.onChangeDate(date);
                }
                return field.onChange(date)
              }}
              timeInputLabel="Horaire:"
              timeFormat="HH:mm"
              dateFormat={props.dateFormat ?? "dd/MM/yyyy HH:mm"}
              wrapperClassName="date-picker"
              popperPlacement="bottom-start"
            />
          </FormControl>
        </div>
      );
    case FormFieldType.DATE_PICKER:
      return (
        <div className="flex rounded-md border border-dark-500 bg-white p-2 text-sm items-center gap-2 font-medium">
          <Image
            src="/icons/calendar.svg"
            height={24}
            width={24}
            alt="user"
            className="ml-2"
          />
          <FormControl>
            <ReactDatePicker
              locale="fr"
              showYearDropdown={true}
              dateFormatCalendar="MMMM"
              yearDropdownItemNumber={70}
              scrollableYearDropdown={true}
              placeholderText={props.placeholder}
              isClearable={true}
              selected={field.value}
              onChange={(date: Date|null) => {
                if (props.onChangeDate) {
                  props.onChangeDate(date);
                }
                return field.onChange(date)
              }}
              dateFormat={props.dateFormat ?? "dd/MM/yyyy"}
              wrapperClassName="date-picker"
            />
          </FormControl>
        </div>
      );
    case FormFieldType.SELECT:
      return (
        <FormControl >
          <Select 
            onValueChange={(value) => {
              if (props.onChangeInput) {
                props.onChangeInput(value);
              }
              return field.onChange(value)
            }}
            defaultValue={field.value}>
            <FormControl>
              <SelectTrigger className="shad-select-trigger">
                <SelectValue placeholder={props.placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent className=" bg-white">
              {props.children}
            </SelectContent>
          </Select>
        </FormControl>
      );
    case FormFieldType.SWITCH:
      return (
        <FormControl>
          <div className="flex items-center gap-4">
            <Switch
              id={props.name}
              checked={field.value}
              onCheckedChange={field.onChange}
            />

            <label htmlFor={props.name} className="checkbox-label">
              {props.label}
            </label>
          </div>
        </FormControl>
      );

    case FormFieldType.SKELETON:
      return props.renderSkeleton ? props.renderSkeleton(field) : null;
    default:
      return null;
  }
};

const CustomFormField = (props: CustomProps) => {
  const { control, name, label } = props;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex-1">
          {props.fieldType !== FormFieldType.CHECKBOX && label && (
            <FormLabel className="shad-input-label">{label}</FormLabel>
          )}
          <RenderInput field={field} props={props} />

          <FormMessage className="form-message mt-2" />
        </FormItem>
      )}
    />
  );
};

export default CustomFormField;
